import { isEmpty } from '../../util/typeGuards';

import { LogLevel, LogType } from './types';

export class LoggerService {
  private static logger: LoggerService;

  private level: LogLevel = LogLevel.Error;

  private logTypes: LogType[] = [ LogType.Console ];

  private errorMessages: any[] = [];

  private crashErrorMessages: any[] = [];

   
  private constructor() {
    // In order to avoid having multiple instances constructor is set as private
  }

  static getLogger(): LoggerService {
    if (!this.logger) {
      this.logger = new LoggerService();
    }
    return this.logger;
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  setType(logType: LogType): void {
    this.logTypes = [ logType ];
  }

  addType(logType: LogType): void {
    this.logTypes.push(logType);
  }

  resetToDefault(): void {
    this.logTypes = [ LogType.Console ];
    this.level = LogLevel.Error;
    this.errorMessages = [];
    this.crashErrorMessages = [];
  }

  private getMessageWithDate(message: any): string {
    return `${new Date().toISOString()} - ${message}`;
  }

  log(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Info) {
      return;
    }

    this.consoleLogger(console.log, message, params);
    this.fileLogger(message, params);
  }

  trace(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Trace) {
      return;
    }

    this.consoleLogger(console.trace, message, params);
    this.fileLogger(message, params);
  }

  debug(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Debug) {
      return;
    }

    this.consoleLogger(console.debug, message, params);
    this.fileLogger(message, params);
  }

  info(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Info) {
      return;
    }

    this.consoleLogger(console.info, message, params);
    this.fileLogger(message, params);
  }

  warn(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Warn) {
      return;
    }

    this.consoleLogger(console.warn, message, params);
    this.fileLogger(message, params);
  }

  error(message?: any, ...params: any[]): void {
    if (this.level < LogLevel.Error) {
      return;
    }

    this.consoleLogger(console.error, message, params);
    this.fileLogger(message, params);
    this.storeErrorMessage(message);
  }

  errorBoundary(message?: any): void {
    const formattedMessage = `[ErrorBoundary] ${message}`;
    this.storeCrashErrorMessage(formattedMessage); // TODO: ACE-448 send to firebase
  }

  private storeErrorMessage(message: any): void {
    this.errorMessages.push(message);
  }

  private storeCrashErrorMessage(message: any): void {
    this.crashErrorMessages.push(message);
  }

  private consoleLogger(loggerFunction: Function, message: any, params: any[]): void {
    if (!this.logTypes.includes(LogType.Console)) {
      return;
    }
    this.printLog(loggerFunction ?? console.log, message, params);
  }

  private printLog(loggerFunction: Function, message: any, params: any[]): void {
    const prefix = '[AlaricLogger] ';
    if (isEmpty(params)) {
      loggerFunction(`${prefix}${this.getMessageWithDate(message)}`);
      return;
    }

    loggerFunction(`${prefix}${this.getMessageWithDate(message)}`, params);
  }

  private fileLogger(message: any, ...params: any[]): void {
    if (!this.logTypes.includes(LogType.File)) {
      return;
    }
    throw new Error('Not implemented yet!');
  }
}
